/**
 * Periods, dates, the `Clock` interface and date formatting. The single source of
 * truth (`.docs/00-index.md` §5): no `new Date()` and no `Date.now()` outside this
 * file, the `Clock` implementations in `apps/api/src/integrations/` and tests —
 * ESLint enforces it, and this is one of the two allow-listed files.
 *
 * No date library. `Intl` formats; the arithmetic is UTC and done by hand, because
 * every rule the portal has is a UTC rule:
 *
 * `ARCHITECTURE.md` §Conventions: "A usage event's period is its `occurred_at`
 * month in UTC — one rule, stated once." This file is where it is stated.
 */
import { DomainError } from './errors.js';
import { dateTimeFormat, partOf } from './intl.js';
/**
 * Years run 1000–9999 deliberately: `Date.UTC` remaps years 0–99 onto 1900–1999,
 * and a period that silently became 1926 would be a very quiet bug.
 */
export const PERIOD_PATTERN = /^([1-9]\d{3})-(0[1-9]|1[0-2])$/;
export const DATE_ONLY_PATTERN = /^([1-9]\d{3})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const MILLISECONDS_PER_SECOND = 1_000;
const MILLISECONDS_PER_MINUTE = 60_000;
const SECONDS_PER_MINUTE = 60;
const MILLISECONDS_PER_HOUR = 3_600_000;
const MILLISECONDS_PER_DAY = 86_400_000;
/** The mockups are day-first: `6 Aug 2026`, `Thursday 3 September`, `14:22`. */
export const DATE_LOCALE = 'en-GB';
/* --------------------------------------------------------------------- internals */
function assertValidDate(instant) {
    if (Number.isNaN(instant.getTime())) {
        throw new DomainError('ValidationFailed', 'That is not a point in time.', {});
    }
}
function toDate(value) {
    const instant = typeof value === 'string' ? new Date(value) : new Date(value.getTime());
    assertValidDate(instant);
    return instant;
}
function pad(value, width) {
    return String(value).padStart(width, '0');
}
/** Days in a month, from a 1-based month number. */
function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function periodParts(value) {
    const match = PERIOD_PATTERN.exec(value);
    if (match === null) {
        throw new DomainError('ValidationFailed', `"${value}" is not a period.`, { value });
    }
    return { year: Number(match[1]), month: Number(match[2]) };
}
/**
 * The one reader for a date string: the pattern, then the calendar. `2026-02-31`
 * matches the pattern and is still not a day, so both checks live together and
 * `isDateOnly` and `dateOnlyParts` cannot drift apart.
 */
function readDateOnly(value) {
    const match = DATE_ONLY_PATTERN.exec(value);
    if (match === null)
        return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    return day <= daysInMonth(year, month) ? { year, month, day } : null;
}
function dateOnlyParts(value) {
    const parts = readDateOnly(value);
    if (parts === null) {
        throw new DomainError('ValidationFailed', `"${value}" is not a date.`, { value });
    }
    return parts;
}
/* ----------------------------------------------------------------------- periods */
export function period(year, month) {
    if (!Number.isSafeInteger(year) || year < 1000 || year > 9999) {
        throw new DomainError('ValidationFailed', 'A year is between 1000 and 9999.', {
            year: String(year),
        });
    }
    if (!Number.isSafeInteger(month) || month < 1 || month > 12) {
        throw new DomainError('ValidationFailed', 'A month is between 1 and 12.', {
            month: String(month),
        });
    }
    return `${String(year)}-${pad(month, 2)}`;
}
export function isPeriod(value) {
    return PERIOD_PATTERN.test(value);
}
export function parsePeriod(value) {
    if (!isPeriod(value)) {
        throw new DomainError('ValidationFailed', `"${value}" is not a period — periods are YYYY-MM.`, {
            value,
        });
    }
    return value;
}
/**
 * The **UTC** month an instant falls in. One rule, stated once: an event at
 * `2026-08-31T23:30:00-04:00` is 03:30 UTC on 1 September, so it belongs to
 * `2026-09`.
 */
export function periodOf(instant) {
    assertValidDate(instant);
    return period(instant.getUTCFullYear(), instant.getUTCMonth() + 1);
}
export function periodOfDateOnly(value) {
    const { year, month } = dateOnlyParts(value);
    return period(year, month);
}
export function nextPeriod(value) {
    const { year, month } = periodParts(value);
    return month === 12 ? period(year + 1, 1) : period(year, month + 1);
}
export function previousPeriod(value) {
    const { year, month } = periodParts(value);
    return month === 1 ? period(year - 1, 12) : period(year, month - 1);
}
/**
 * The first month a retainer started on `startedOn` bills in full.
 *
 * `.docs/12-retainers.md` §Edge cases: "`start` mid-month: `started_on` is the signup
 * date; the first period is the first full month… `start` on the 1st: no proration."
 * A signup on 4 March bills March prorated and **April** in full, so the run the
 * signup invoice is for is April's.
 *
 * Here rather than in `modules/retainers/service.ts`, where it was written, because
 * session 20's onboarding wizard has to name the period it runs `billing_run$` with
 * and `apps/web` may take date maths from this file and nowhere else. The rule is one
 * line, which is exactly the kind of thing `.docs/00-index.md` §5 keeps in one place —
 * the SPA's copy of it was a second statement of "what proration means".
 */
export function firstFullMonth(startedOn) {
    const period = periodOfDateOnly(startedOn);
    return startedOn.endsWith('-01') ? period : nextPeriod(period);
}
/** `YYYY-MM` sorts lexicographically, which is the whole reason for the format. */
export function comparePeriods(a, b) {
    if (a < b)
        return -1;
    if (a > b)
        return 1;
    return 0;
}
/** Every period from `from` to `to`, both ends included. */
export function periodsBetween(from, to) {
    if (comparePeriods(from, to) > 0) {
        throw new DomainError('ValidationFailed', 'A period range runs forwards.', { from, to });
    }
    const periods = [];
    let current = from;
    while (comparePeriods(current, to) <= 0) {
        periods.push(current);
        current = nextPeriod(current);
    }
    return periods;
}
export function daysInPeriod(value) {
    const { year, month } = periodParts(value);
    return daysInMonth(year, month);
}
/** UTC midnight on the 1st. */
export function periodStart(value) {
    const { year, month } = periodParts(value);
    return new Date(Date.UTC(year, month - 1, 1));
}
/**
 * The last instant of the period, `23:59:59.999` UTC on its last day. A half-open
 * range — which is what a query wants — is `periodStart(p)` to
 * `periodStart(nextPeriod(p))`.
 */
export function periodEnd(value) {
    const { year, month } = periodParts(value);
    return new Date(Date.UTC(year, month, 0, 23, 59, 59, 999));
}
/**
 * The billing day of a period: the 1st, as a calendar date.
 *
 * `SERVICES.md` §Retainers: "Billing day: 1st of the month." A `DateOnly` rather than
 * an instant, because that is what a `date` column, an `effective_on` and an
 * `issue_on` all are — and turning a period into a day was written twice (sessions 12
 * and 14) before it was written here.
 */
export function firstDayOf(value) {
    const { year, month } = periodParts(value);
    return dateOnly(year, month, 1);
}
/** The last calendar day of a period. What a scheduled cancellation ends on. */
export function lastDayOf(value) {
    const { year, month } = periodParts(value);
    return dateOnly(year, month, daysInMonth(year, month));
}
/* --------------------------------------------------------------------- date only */
export function dateOnly(year, month, day) {
    const asPeriod = period(year, month);
    const length = daysInPeriod(asPeriod);
    if (!Number.isSafeInteger(day) || day < 1 || day > length) {
        throw new DomainError('ValidationFailed', `${asPeriod} has ${String(length)} days.`, {
            day: String(day),
            period: asPeriod,
        });
    }
    return `${asPeriod}-${pad(day, 2)}`;
}
/** Refuses 2026-02-31: the pattern is not enough, the calendar has the last word. */
export function isDateOnly(value) {
    return readDateOnly(value) !== null;
}
export function parseDateOnly(value) {
    if (!isDateOnly(value)) {
        throw new DomainError('ValidationFailed', `"${value}" is not a date — dates are YYYY-MM-DD.`, {
            value,
        });
    }
    return value;
}
/** The calendar date an instant falls on in a given IANA zone. */
export function dateOnlyOf(instant, zone) {
    assertValidDate(instant);
    const parts = dateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    return parseDateOnly(`${partOf(parts, 'year')}-${partOf(parts, 'month')}-${partOf(parts, 'day')}`);
}
/**
 * A calendar date, `days` days later. Negative goes backwards.
 *
 * Calendar arithmetic, not clock arithmetic: `Date.UTC` on the parts, so no offset,
 * no daylight saving and no hour of the day is involved. That is what makes it the
 * right tool for "due in seven days" — a rule about days on a wall calendar
 * (`.docs/07-tasks.md` §Edge cases) rather than about 168 hours.
 */
export function addDaysToDateOnly(value, days) {
    if (!Number.isSafeInteger(days)) {
        throw new DomainError('ValidationFailed', 'A day offset is a whole number.', {
            days: String(days),
        });
    }
    const { year, month, day } = dateOnlyParts(value);
    const moved = new Date(Date.UTC(year, month - 1, day) + days * MILLISECONDS_PER_DAY);
    return dateOnly(moved.getUTCFullYear(), moved.getUTCMonth() + 1, moved.getUTCDate());
}
/**
 * A calendar date, `days` **business** days later. Saturdays and Sundays do not
 * count; nothing else is a holiday, because this system serves several markets and
 * has no calendar for any of them.
 *
 * `.docs/11-work-orders.md` §Edge cases is what needs it: a request the rate card has
 * no unit for is quoted by hand, and the client is promised an answer in "+2 business
 * days". Two days on from a Friday is the following Tuesday, and a promise that lands
 * on a Sunday is a promise nobody reads.
 *
 * Zero is the day itself, weekend or not — "due today" is a statement about today. A
 * negative count walks backwards by the same rule. Calendar arithmetic, like
 * `addDaysToDateOnly` above: no offset, no daylight saving, no hour of the day.
 */
export function addBusinessDays(value, days) {
    if (!Number.isSafeInteger(days)) {
        throw new DomainError('ValidationFailed', 'A business-day offset is a whole number.', {
            days: String(days),
        });
    }
    const step = days < 0 ? -1 : 1;
    let remaining = days < 0 ? -days : days;
    let moved = parseDateOnly(value);
    while (remaining > 0) {
        moved = addDaysToDateOnly(moved, step);
        if (isWeekday(moved))
            remaining -= 1;
    }
    return moved;
}
/**
 * The day of the week a calendar date falls on: 0 for Sunday, 6 for Saturday.
 *
 * `getUTCDay`'s own numbering, deliberately, because that is what a reader checks it
 * against — and it is what `availability_rule.weekday` stores (session 16), so the
 * column and this function cannot disagree about which day is 0. The one place this
 * file reads a day of the week.
 */
export function weekdayOf(value) {
    const { year, month, day } = dateOnlyParts(value);
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}
/** Monday to Friday. */
export function isWeekday(value) {
    const weekday = weekdayOf(value);
    return weekday !== SUNDAY && weekday !== SATURDAY;
}
const SUNDAY = 0;
const SATURDAY = 6;
/**
 * How far ahead of UTC a zone is at an instant, in milliseconds. `Asia/Dubai` is
 * `+14_400_000` all year.
 *
 * `Intl` is the only tzdata this system has, so the offset is read by formatting the
 * instant in the zone and subtracting. `hour % 24` because ICU renders midnight as
 * `24` under `hour12: false` in some versions, and 24:30 is not a time.
 */
/**
 * How far ahead of UTC a zone is at an instant, in whole minutes.
 *
 * `mockups/client/calls.html` prints "UTC · UTC+0" beside "America/New_York ·
 * UTC−4", and `BookingRulesView` carried the two IANA names and no offset — which the
 * session 16 report lists under what the routes cannot produce. The **number** is what
 * crosses the wire: the sign, the glyph and whether to print the minutes are the SPA's
 * (`.docs/00-index.md` §5 puts rendering in `format/useFormat.ts`), and a page that was
 * handed `"UTC+3"` could not print `UTC+05:45` for Kathmandu without parsing it back.
 *
 * It takes an instant because an offset is a thing that changes twice a year in New
 * York and never in UTC — the caller passes the day the fold is being read on.
 */
export function utcOffsetMinutes(instant, zone) {
    // Rounded because tzdata carries second-level LMT offsets for instants before a
    // zone's first standard time, and `BookingRulesView` declares a `z.int()`. Every
    // instant this is called with is contemporary, so it is a guard rather than a
    // conversion — but a 500 on the booking page is a poor way to learn that.
    return Math.round(zoneOffsetMs(instant, zone) / 60_000);
}
function zoneOffsetMs(instant, zone) {
    const parts = dateTimeFormat('en-CA', {
        timeZone: zone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    }).formatToParts(instant);
    const wall = Date.UTC(Number(partOf(parts, 'year')), Number(partOf(parts, 'month')) - 1, Number(partOf(parts, 'day')), Number(partOf(parts, 'hour')) % 24, Number(partOf(parts, 'minute')), Number(partOf(parts, 'second')));
    // The instant's own milliseconds are not in the parts and are not in `wall`
    // either, so they have to come off the other side or the offset is out by them.
    return wall - (instant.getTime() - instant.getMilliseconds());
}
/**
 * The instant at a wall-clock time, on a calendar day, in a zone.
 *
 * The general case, and the only copy of the conversion in this repository.
 * `startOfDayIn` and `endOfDayIn` below are this at `00:00:00.000` and
 * `23:59:59.999`; session 16's availability rules are wall times in the studio's zone
 * — "all day, Mon–Fri" on `mockups/client/calls.html` — and are the third caller.
 *
 * Two passes: the wall time is converted with the offset in force at the first guess,
 * and then again with the offset in force at the answer. One pass is wrong across a
 * daylight-saving boundary, which UTC never has and New York has twice a year —
 * and a booking grid drawn an hour out on 1 November is the failure
 * `.docs/16-files-calls-reports.md` §Edge cases names first.
 *
 * A wall time that does not exist — the hour a zone skips forward over — lands on the
 * instant the clock jumped to; one that happens twice lands on the **second** of them,
 * because the second pass reads the offset in force *after* the change. Neither needs a
 * rule here: both are a real instant, both are outside every window this system offers,
 * and `dates.test.ts` pins them so the answer is a decision rather than a coincidence.
 * (The first version of this comment claimed the first of them, which is the opposite
 * of what the two passes do — a test written against the prose is what found it.)
 */
export function instantAtWallTime(day, zone, time) {
    const { year, month, day: date } = dateOnlyParts(day);
    const wall = Date.UTC(year, month - 1, date, time.hour, time.minute, time.second, time.millisecond);
    const guess = new Date(wall - zoneOffsetMs(new Date(wall), zone));
    return new Date(wall - zoneOffsetMs(guess, zone));
}
/**
 * The first millisecond of a calendar day, in a zone, as an instant.
 *
 * The other end of `endOfDayIn`, and the two together are the window a board's "due
 * this week" is counted over (`readmodels/tasks-board.ts`).
 */
export function startOfDayIn(day, zone) {
    return instantAtWallTime(day, zone, { hour: 0, minute: 0, second: 0, millisecond: 0 });
}
/**
 * The last millisecond of a calendar day, in a zone, as an instant.
 *
 * A due date is a **day**, and a task due on the 21st is late on the 22nd — so
 * `due_at < now` (which is how `overdue` is derived, never stored) has to compare
 * against the end of the day rather than its start. The zone is the studio's, because
 * that is whose working day it is.
 */
export function endOfDayIn(day, zone) {
    return instantAtWallTime(day, zone, {
        hour: 23,
        minute: 59,
        second: 59,
        millisecond: 999,
    });
}
/**
 * Days from this date to the end of its month, counting this one. 4 March is 28,
 * which is the numerator of the signup proration on `FES-2026-011`.
 */
export function daysRemainingInclusive(value) {
    const { year, month, day } = dateOnlyParts(value);
    return daysInMonth(year, month) - day + 1;
}
/**
 * What day it is, in a zone. `Clock` in, `DateOnly` out.
 *
 * Six modules had written `dateOnlyOf(deps.clock.now(), deps.config.studioTimezone)`
 * out in full, each behind a local `today(deps)` — and the eleven-site version of it,
 * `periodIn` below, had grown a genuine trap: `usage`'s answered in **UTC** under the
 * same name and signature as everybody else's, which is three hours and, on the 1st
 * of the month, a whole month. The zone is a required argument here for that reason:
 * a caller has to say which one it means.
 */
export function todayIn(clock, zone) {
    return dateOnlyOf(clock.now(), zone);
}
/** The month it is, in a zone. `todayIn`'s period, and the same argument applies. */
export function periodIn(clock, zone) {
    return periodOfDateOnly(todayIn(clock, zone));
}
/** A clock stopped at one instant. Each `now()` hands back a fresh `Date`. */
export function fixedClock(instant) {
    const fixed = toDate(instant);
    return { now: () => new Date(fixed.getTime()) };
}
export function advancingClock(start) {
    let current = toDate(start);
    function advance(milliseconds) {
        if (!Number.isSafeInteger(milliseconds)) {
            throw new DomainError('ValidationFailed', 'A clock advances by whole milliseconds.', {
                milliseconds: String(milliseconds),
            });
        }
        current = new Date(current.getTime() + milliseconds);
    }
    return {
        now: () => new Date(current.getTime()),
        advance,
        advanceDays: (days) => {
            advance(days * MILLISECONDS_PER_DAY);
        },
        set: (instant) => {
            current = toDate(instant);
        },
    };
}
/* ------------------------------------------------------------------- arithmetic */
export function addDays(instant, days) {
    assertValidDate(instant);
    if (!Number.isSafeInteger(days)) {
        throw new DomainError('ValidationFailed', 'A day offset is a whole number.', {
            days: String(days),
        });
    }
    return new Date(instant.getTime() + days * MILLISECONDS_PER_DAY);
}
export function addSeconds(instant, seconds) {
    assertValidDate(instant);
    if (!Number.isSafeInteger(seconds)) {
        throw new DomainError('ValidationFailed', 'A second offset is a whole number.', {
            seconds: String(seconds),
        });
    }
    return new Date(instant.getTime() + seconds * MILLISECONDS_PER_SECOND);
}
export function addHours(instant, hours) {
    assertValidDate(instant);
    if (!Number.isSafeInteger(hours)) {
        throw new DomainError('ValidationFailed', 'An hour offset is a whole number.', {
            hours: String(hours),
        });
    }
    return new Date(instant.getTime() + hours * MILLISECONDS_PER_HOUR);
}
/**
 * Midnight UTC on the day `instant` falls in.
 *
 * The window session 06's Gmail counters are taken over —
 * `.docs/06-jobs-mail-storage.md` §Edge cases: "the counter is by UTC day from the
 * outbox". UTC and not the studio zone, because it is Google's day that resets, and
 * Google counts in Pacific… which is to say: it is an approximation either way, and
 * the one that is the same in every process is the one worth having.
 */
export function startOfUtcDay(instant) {
    assertValidDate(instant);
    return new Date(Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate()));
}
/**
 * The next UTC midnight after `instant`, and never `instant` itself.
 *
 * Session 05's daily agent budget resets here, and the `retry_after` it hands back
 * is the distance to it. Exactly midnight has to move to the *next* one: a budget
 * that said "retry in 0 seconds" would be a client in a spin loop.
 */
export function startOfNextUtcDay(instant) {
    assertValidDate(instant);
    const day = Date.UTC(instant.getUTCFullYear(), instant.getUTCMonth(), instant.getUTCDate());
    return new Date(day + MILLISECONDS_PER_DAY);
}
/**
 * Whole seconds since the epoch — a JWT's `iat` and `exp`.
 *
 * Here rather than at the call site because it truncates, and `Math.trunc` is
 * lint-banned outside this file and `money.ts` (`.docs/00-index.md` §5). Truncation
 * and not rounding: a JWT issued at `x.9` seconds and stamped `x+1` is a JWT from the
 * future, which every verifier including Google's rejects.
 */
export function epochSeconds(instant) {
    assertValidDate(instant);
    return Math.trunc(instant.getTime() / MILLISECONDS_PER_SECOND);
}
/**
 * Whole days from `from` to `to`, rounded **down**, and never negative.
 *
 * "How many days has this quote been sitting with them" — session 11's seven-day
 * chase, which is what `ARCHITECTURE.md` §The task queue means by "unanswered for 7
 * days". Down rather than to nearest, because a quote sent seven days and four hours
 * ago has been waiting seven days and saying eight would be wrong in the direction
 * that reads as a mistake.
 *
 * Here rather than at the call site for the reason every division is here: `Math.floor`
 * is lint-banned outside this file and `money.ts` (`eslint.config.js`).
 */
export function daysBetween(from, to) {
    assertValidDate(from);
    assertValidDate(to);
    const elapsed = to.getTime() - from.getTime();
    return elapsed <= 0 ? 0 : Math.floor(elapsed / MILLISECONDS_PER_DAY);
}
/**
 * Seconds from `from` to `to`, fractional, and never negative.
 *
 * Fractional because the one thing that prints it wants a tenth of a second — the
 * billing run's own duration, `studio/money.html`'s "4.2 s" — and rounding here would
 * make that impossible to reach without a second division somewhere a lint rule
 * forbids one. How many decimals to show is the formatter's decision, not this
 * function's.
 *
 * Clamped at zero for the same reason `daysBetween` is: two stamps out of order are a
 * clock that moved, not a negative duration.
 */
export function secondsBetween(from, to) {
    assertValidDate(from);
    assertValidDate(to);
    const elapsed = to.getTime() - from.getTime();
    return elapsed <= 0 ? 0 : elapsed / MILLISECONDS_PER_SECOND;
}
/** Whole seconds from `instant` to the next UTC midnight, rounded up. Never zero. */
export function secondsUntilNextUtcDay(instant) {
    const remaining = startOfNextUtcDay(instant).getTime() - instant.getTime();
    return Math.ceil(remaining / MILLISECONDS_PER_SECOND);
}
export function addMinutes(instant, minutes) {
    assertValidDate(instant);
    if (!Number.isSafeInteger(minutes)) {
        throw new DomainError('ValidationFailed', 'A minute offset is a whole number.', {
            minutes: String(minutes),
        });
    }
    return new Date(instant.getTime() + minutes * MILLISECONDS_PER_MINUTE);
}
/**
 * A countdown, as `m:ss`.
 *
 * `mockups/client/login.html` renders the resend button as "Resend in 0:44", and
 * that string is the only place in the product where a duration is shown as one.
 * It is here rather than in the SPA for the reason every division is here: the
 * repository bans `Math.floor` outside this file and `money.ts`
 * (`eslint.config.js`), so a component that split seconds into minutes and seconds
 * could not be written — which is the rule working, not a rule in the way.
 *
 * Seconds are rounded **up**: with 200 milliseconds left the button still says
 * `0:01`, because a countdown that shows `0:00` for a fifth of a second while
 * still refusing the click reads as a broken button. It never goes below `0:00`,
 * so a deadline in the past is simply over.
 */
export function formatCountdown(deadline, now) {
    assertValidDate(deadline);
    assertValidDate(now);
    const remaining = deadline.getTime() - now.getTime();
    const seconds = remaining <= 0 ? 0 : Math.ceil(remaining / MILLISECONDS_PER_SECOND);
    const minutes = (seconds - (seconds % SECONDS_PER_MINUTE)) / SECONDS_PER_MINUTE;
    return `${String(minutes)}:${pad(seconds % SECONDS_PER_MINUTE, 2)}`;
}
/**
 * How long ago an instant was, in words: `just now`, `12 minutes ago`, `3 hours ago`,
 * `yesterday`, `5 days ago`, `2 months ago`, `a year ago`.
 *
 * Session 46's, for a forms inbox: a lead is read by how fresh it is, and "received
 * 3 hours ago" beside the date is the sentence a person checking for new enquiries is
 * looking for. Here rather than in the SPA for `formatCountdown`'s reason — every
 * division into whole units is a `Math.floor`, and that is banned outside this file.
 *
 * **Down, never to the nearest**, for `daysBetween`'s reason: a lead that arrived 47
 * minutes ago has not been waiting an hour. Days are 24-hour spans, not calendar days,
 * so it needs no zone and "yesterday" means 24 to 48 hours. A month is 30 days and a
 * year 365 — the coarse end of a relative date is approximate by nature, and the
 * absolute date always stands beside it. An instant in the future (a clock that moved)
 * is "just now", never a negative age.
 */
export function formatAgo(instant, now) {
    assertValidDate(instant);
    assertValidDate(now);
    const elapsed = now.getTime() - instant.getTime();
    if (elapsed < MILLISECONDS_PER_MINUTE)
        return 'just now';
    const minutes = Math.floor(elapsed / MILLISECONDS_PER_MINUTE);
    if (minutes < 60)
        return ago(minutes, 'minute', 'a minute ago');
    const hours = Math.floor(elapsed / MILLISECONDS_PER_HOUR);
    if (hours < 24)
        return ago(hours, 'hour', 'an hour ago');
    const days = Math.floor(elapsed / MILLISECONDS_PER_DAY);
    if (days < 30)
        return ago(days, 'day', 'yesterday');
    if (days < 365)
        return ago(Math.floor(days / 30), 'month', 'a month ago');
    return ago(Math.floor(days / 365), 'year', 'a year ago');
}
/** `5 days ago`, or the singular's own words for one. */
function ago(count, unit, one) {
    return count === 1 ? one : `${String(count)} ${unit}s ago`;
}
/**
 * Is `a` an earlier day than `b`?
 *
 * A `DateOnly` is fixed-width `YYYY-MM-DD` with zero-padded parts, so `<` on the
 * strings **is** chronological order — which is also why `ORDER BY` on the `date`
 * column agrees with it. Here rather than at the two call sites, because a bare `<`
 * between two branded strings reads like a mistake and the next person would
 * "fix" it into a pair of `Date`s and a time zone question that does not exist.
 */
export function isDayBefore(a, b) {
    return parseDateOnly(a) < parseDateOnly(b);
}
export function isBefore(a, b) {
    assertValidDate(a);
    assertValidDate(b);
    return a.getTime() < b.getTime();
}
export function isAfter(a, b) {
    assertValidDate(a);
    assertValidDate(b);
    return a.getTime() > b.getTime();
}
/** `SERVICES.md` §Retainers: "Billing day: 1st of the month." In UTC. */
export function isBillingDay(instant) {
    assertValidDate(instant);
    return instant.getUTCDate() === 1;
}
/** One options object per style. The output is what the mockups render. */
const DATE_STYLE_OPTIONS = {
    /**
     * `9 Jul` — a day inside a document that already says which year it is about.
     *
     * Session 14's, for the approval note on an invoice line: `client/billing.html` §7
     * prints "approved 9 Jul by Deniz Aksoy" beside a line on an invoice whose own
     * period is stated at the top, and repeating the year there is a year nobody reads.
     */
    day: { day: 'numeric', month: 'short' },
    /**
     * `Wed 26 Aug` — a day in a timeline that already says which months it spans.
     *
     * Session 19's, for `client/dashboard.html` §4 and the report's own "Upcoming":
     * both label every row with a short weekday, because what a reader is checking on
     * a six-week timeline is which day of the week a drop lands on. `long` is the same
     * fact spelled out and is what a call confirmation uses.
     */
    weekday: { weekday: 'short', day: 'numeric', month: 'short' },
    /** `6 Aug 2026` */
    short: { day: 'numeric', month: 'short', year: 'numeric' },
    /** `Thursday 3 September` */
    long: { weekday: 'long', day: 'numeric', month: 'long' },
    /** `08:00` */
    time: { hour: '2-digit', minute: '2-digit', hour12: false },
    /**
     * `08:00 EDT` — a time of day with the zone it is in.
     *
     * Session 19's, and `schemas/readmodels/upcoming.ts` already promised it: "the
     * instant it happens … `useFormat()` renders the wall time and offset ('08:00
     * EDT')". A time of day printed with no zone beside a call that two countries are
     * dialling into is the one date format in this system that can be read wrong
     * without looking wrong.
     */
    clock: { hour: '2-digit', minute: '2-digit', hour12: false, timeZoneName: 'short' },
    /**
     * `12 Aug 2026, 14:22` — a date and a time of day **in a zone the reader already
     * knows**.
     *
     * The only callers left are the call mails, which say which zone they are in in
     * the sentence around the value (`scheduling/service.ts`: `"… (${zone})"`) or are
     * written in the studio's own. Anything rendered into a browser wants `stamp`
     * below, because a browser's zone is not a fact the writer knows.
     */
    datetime: {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    },
    /**
     * `12 Aug 2026, 10:22 EDT` — the same instant, saying which clock it is on.
     *
     * Session 24 (R2-07). An approval stamp on `/app/requests` was rendered through
     * `datetime` in whatever zone the reader's laptop happened to be in, on a page
     * whose own copy calls that record the thing that authorises a charge — "Your
     * name and the time are recorded against it, and that record, not an email, is
     * what authorises the charge." A Budapest laptop read New York's 04:47 as 10:47
     * and said nothing about either.
     *
     * The zone stays the reader's (`format/useFormat.ts` — "the zone is the
     * browser's"); what changes is that the value now says so, which is the same
     * decision `clock` made for a call time and for the same reason. Everything that
     * renders a **time of day** into the portal is now one of `clock` or `stamp`, and
     * neither can be read wrong without looking wrong.
     */
    stamp: {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        timeZoneName: 'short',
    },
};
/** An abbreviated month is three letters. See `formatDate`. */
const SHORT_MONTH_LENGTH = 3;
/**
 * A billing period as a person reads it: `2026-08` → `August 2026`.
 *
 * Every mail that names a month prints it this way — `deliverable_published`,
 * `report_issued`, `payment_receipt` and, from session 13, `widget_cap_reached` — and
 * until now each caller passed the string in already written. Session 13 is the first
 * one that has to produce it, so it is here rather than in a module: `Intl` is banned
 * outside this file and `format.ts` (`eslint.config.js`), and a fifth template
 * spelling twelve month names out would be the twelfth copy of a list.
 *
 * UTC, and deliberately: a period is a month rather than an instant, and
 * `periodStart` is midnight on the first — which in any zone west of Greenwich is
 * still the month before.
 */
export function formatPeriod(value) {
    return dateTimeFormat(DATE_LOCALE, {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(periodStart(value));
}
/**
 * A period as a **short** month and year: `2026-08` → `Aug 2026`.
 *
 * `formatPeriod` above is the long form the mails print. This is the one a chart's
 * axis and a table's first column want, and `readmodels/client-metrics.ts` used to
 * reverse-engineer it — `formatDate(periodStart(period), 'UTC', 'short')` gives
 * `"1 Jan 2026"`, so it stripped the leading day with a regex, split on a space, and
 * fell back to slicing `formatPeriod` if the locale had moved the day. That obeyed the
 * letter of "`Intl` is banned outside this file" while re-implementing the thing the
 * rule protects.
 *
 * UTC, for the reason `formatPeriod` is: a period is a month rather than an instant.
 */
export function formatMonth(value) {
    return dateTimeFormat(DATE_LOCALE, {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC',
    })
        .formatToParts(periodStart(value))
        .map((part) => 
    // The same three-letter cut `formatDate` makes, and for the same reason: CLDR
    // abbreviates September as "Sept" and every mockup writes "Sep".
    part.type === 'month' ? part.value.slice(0, SHORT_MONTH_LENGTH) : part.value)
        .join('');
}
/**
 * The one **part** that is not `DATE_LOCALE`'s, and why.
 *
 * `timeZoneName: 'short'` under `en-GB` is an **offset** — `08:00 GMT-4` — and under
 * `en-US` it is the abbreviation the mockups print, `08:00 EDT`.
 *
 * This used to select the locale for the whole string, which was harmless while
 * `clock` was the only style carrying a zone: a 24-hour `HH:mm` is the same in both
 * locales. Session 24's `stamp` carries a **date** as well, and `en-US` writes that
 * date month-first — so `12 Aug 2026, 10:22 EDT` would have come out as
 * `Aug 12, 2026, 10:22 EDT`, breaking "dates are day first" for the one style whose
 * whole job is being unambiguous. The rule is therefore per part rather than per
 * string: `DATE_LOCALE` owns everything, and the zone abbreviation alone is read off
 * an `en-US` formatter. `clock` is byte-for-byte what it was.
 */
const ZONE_NAME_LOCALE = 'en-US';
export function formatDate(instant, zone, style) {
    assertValidDate(instant);
    const options = DATE_STYLE_OPTIONS[style];
    const parts = dateTimeFormat(DATE_LOCALE, { ...options, timeZone: zone }).formatToParts(instant);
    const abbreviation = options.timeZoneName === undefined ? null : zoneAbbreviation(instant, zone, style);
    /*
     * CLDR abbreviates September as "Sept" in every English locale, and every mockup
     * writes "1 Sep 2026". Eleven months of three letters and one of four is not a house
     * style, so an abbreviated month is cut to three — on the month part only, never on
     * the whole string, which would also cut a name.
     */
    return parts
        .map((part) => {
        if (part.type === 'month' && options.month === 'short') {
            return part.value.slice(0, SHORT_MONTH_LENGTH);
        }
        return part.type === 'timeZoneName' && abbreviation !== null ? abbreviation : part.value;
    })
        .join('');
}
/**
 * `EDT`, `GMT+3`, `UTC` — the short name `ZONE_NAME_LOCALE` gives this zone at this
 * instant.
 *
 * A second formatter over the same options with only the locale changed, so the
 * substitution above cannot pick up a name for a different offset than the one the
 * rest of the string was written at — New York's changes twice a year, and reading
 * the abbreviation off a formatter with no date fields would have been a bet that
 * `Intl` resolves "now" the same way. It does not resolve "now" at all: the instant
 * is the argument, and passing the style's own options is what keeps the two reads
 * about the same moment.
 */
function zoneAbbreviation(instant, zone, style) {
    const options = DATE_STYLE_OPTIONS[style];
    return partOf(dateTimeFormat(ZONE_NAME_LOCALE, { ...options, timeZone: zone }).formatToParts(instant), 'timeZoneName');
}
/* ------------------------------------------------------------------- zones */
/**
 * The IANA zone names this runtime knows, canonical spellings only.
 *
 * `Intl.supportedValuesOf('timeZone')` is the list the platform actually has, kept
 * current with the tzdata it was built against — `.docs/04-auth-clients.md` names it
 * as the check for a member's zone. It is built once: the call allocates an array of
 * ~600 strings every time.
 *
 * It is deliberately stricter than `new Intl.DateTimeFormat({ timeZone })`, which
 * also accepts legacy aliases (`US/Eastern`) and POSIX spellings (`EST5EDT`). Two
 * names for one zone means two rows that look different and render the same, and a
 * `GROUP BY` that splits them.
 */
const SUPPORTED_TIME_ZONES = new Set([
    ...Intl.supportedValuesOf('timeZone'),
    /*
     * `UTC` is not in that list — Node reports region zones and leaves out `UTC` and
     * the whole `Etc/*` family. It is added back because three documents in this
     * repository say the API speaks UTC, because every formatter and every database
     * accepts it, and because it is unambiguous in the way `US/Eastern` is not.
     */
    'UTC',
]);
/** Every zone a client or a member may be given, sorted. For a form's options. */
export function timeZones() {
    return [...SUPPORTED_TIME_ZONES];
}
/** True for a canonical IANA zone name, and only for one. */
export function isTimeZone(value) {
    return SUPPORTED_TIME_ZONES.has(value);
}
/** Throws `ValidationFailed` for anything `isTimeZone` refuses. */
export function assertTimeZone(value) {
    if (!isTimeZone(value)) {
        throw new DomainError('ValidationFailed', 'That is not an IANA time zone. UTC and America/New_York are.', { value });
    }
    return value;
}
//# sourceMappingURL=dates.js.map